import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Instrument cursor: a flare tick that opens into a labelled ring over
 * interactive elements. Position uses a spring (interruptible, so Framer Motion
 * stays the right tool here); target detection is delegated to pointerover
 * instead of running closest() on every mousemove frame.
 *
 * Only mounts on fine pointers with motion allowed — and it is what adds
 * .cursor-hidden, so if this never mounts the native cursor stays.
 */
const Cursor = () => {
  const [active, setActive] = useState(false);
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("idle"); // idle | link | label
  const [label, setLabel] = useState("");
  const [pressed, setPressed] = useState(false);

  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 700, damping: 38, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 700, damping: 38, mass: 0.4 });

  const frame = useRef(0);

  useEffect(() => {
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const still = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!fine || still) return undefined;

    setActive(true);
    document.documentElement.classList.add("cursor-hidden");

    const onMove = (e) => {
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        x.set(e.clientX);
        y.set(e.clientY);
        frame.current = 0;
      });
      if (!visible) setVisible(true);
    };

    const onOver = (e) => {
      const hit = e.target instanceof Element
        ? e.target.closest("a, button, [role='button'], input, textarea, [data-cursor-label]")
        : null;

      if (!hit) {
        setMode("idle");
        setLabel("");
        return;
      }

      const text = hit.getAttribute("data-cursor-label");
      if (text) {
        setLabel(text);
        setMode("label");
      } else {
        setLabel("");
        setMode("link");
      }
    };

    const onDown = () => setPressed(true);
    const onUp = () => setPressed(false);
    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("pointerover", onOver, { passive: true });
    window.addEventListener("mousedown", onDown, { passive: true });
    window.addEventListener("mouseup", onUp, { passive: true });
    document.documentElement.addEventListener("mouseleave", onLeave);
    document.documentElement.addEventListener("mouseenter", onEnter);

    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      document.documentElement.classList.remove("cursor-hidden");
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("pointerover", onOver);
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
      document.documentElement.removeEventListener("mouseleave", onLeave);
      document.documentElement.removeEventListener("mouseenter", onEnter);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!active) return null;

  const size = mode === "label" ? 92 : mode === "link" ? 46 : 10;

  return (
    <div className="pointer-events-none fixed inset-0 z-cursor hidden md:block" aria-hidden="true">
      <motion.div
        className="absolute top-0 left-0 flex items-center justify-center"
        style={{ x: sx, y: sy, translateX: "-50%", translateY: "-50%" }}
        animate={{ opacity: visible ? 1 : 0 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className="flex items-center justify-center border border-flare"
          animate={{
            width: size,
            height: mode === "label" ? 28 : size,
            backgroundColor: mode === "idle" ? "var(--flare)" : "rgba(216,30,5,0.08)",
            scale: pressed ? 0.82 : 1,
          }}
          transition={{ type: "spring", stiffness: 520, damping: 34, mass: 0.5 }}
        >
          {mode === "label" && (
            <span className="t-label whitespace-nowrap text-flare" style={{ letterSpacing: "0.14em" }}>
              {label}
            </span>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default Cursor;
